import { useCallback, useEffect, useRef, useState } from "react";
import type { Channel } from "@hybrid-iptv/types";
import { CHANNEL_ROW_HEIGHT } from "./ChannelRow.js";
import { EPG_TIMELINE_HEADER_HEIGHT } from "./EpgScrollRail.js";
import { epgWindowStart, requestGuideFar } from "./hooks.js";
import type { ChannelProgramIndex } from "./hooks.js";

/**
 * How soon after the ring last moved an arrow still counts as the same run of
 * presses. A held key repeats every 50-100 ms; a viewer coming back to the
 * guide (from full screen, a popup, another screen) has been away far longer.
 */
const HELD_RUN_MS = 700;

/**
 * Roving D-pad focus for the guide (TV-first roadmap): ArrowUp/Down move
 * between rows and ArrowLeft/Right between the program blocks in a row.
 * `col === -1` means focus sits on the channel rail (left list); `col >= 0`
 * is the index of a program block within that channel's visible window.
 *
 * Moving focus is ALL this does: it never tunes anything. Highlighting a row
 * or a block used to zap the channel and raise the mini-EPG card, which made
 * the guide impossible to scroll on a remote — every step down the list
 * restarted the stream and covered the grid. Tuning is now an explicit act:
 * OK/Enter on the focused row or block (ChannelRow / EpgProgramBlock), which
 * is also what brings up the detail card (owner, 2026-09-21).
 *
 * The row and column are STATE (not derived from the DOM) so mouse clicks and
 * keyboard agree, and the focused element is located by data attributes after
 * the virtualizer renders the row. Focus is never stolen on mount — the
 * scroll/focus pass only runs after the user has moved focus with arrows or
 * clicked a guide element, and never while a dialog holds focus.
 */
export interface GuideFocusOptions {
  /** Channels shown in the guide (category-filtered) — focus rows index this. */
  channels: Channel[];
  programIndex: ChannelProgramIndex | null;
  timeWindowHours: number;
  /** The shared scroll rail both virtualized panes are driven by. */
  sharedScrollRef: React.RefObject<HTMLDivElement | null>;
  /** Row to start from (e.g. the selected channel's row). Default 0. */
  initialRow?: number;
}

export interface GuideFocus {
  /** Current D-pad target position; col === -1 is the channel rail. */
  focus: { row: number; col: number };
  /** Move focus programmatically (used to sync a mouse click on a row/block). */
  focusAt: (row: number, col: number) => void;
  /** Attach to the guide container; arrows bubble here from rows and blocks. */
  handleKeyDown: (event: React.KeyboardEvent) => void;
}

/** The rail controls whose Right goes to the preview window (owner, 2026-10-06). */
const PREVIEW_RAIL_ITEMS =
  '.navrail__toggle, .navrail a[href="/"], .navrail a[href="/live"], .navrail a[href="/movies"], .navrail a[href="/series"]';

export function useGuideFocus({
  channels,
  programIndex,
  timeWindowHours,
  sharedScrollRef,
  initialRow = 0,
}: GuideFocusOptions): GuideFocus {
  const [focus, setFocus] = useState({ row: initialRow, col: -1 });
  const movedRef = useRef(false);
  /** When the ring last moved: a quick run of presses (a held key) keeps moving through a lost focus. */
  const lastMoveAt = useRef(0);

  /** The programmes a row shows, in order — the same window EpgGrid draws. */
  const programsIn = useCallback(
    (row: number) => {
      const channel = channels[row];
      if (!programIndex || !channel) return [];
      const start = epgWindowStart(new Date());
      const end = new Date(start.getTime() + timeWindowHours * 3600_000);
      return programIndex.getProgramsInWindow(channel.id, start, end);
    },
    [programIndex, channels, timeWindowHours],
  );
  /**
   * How many columns a row has. A row with nothing in the window still has one,
   * its "No information" block (EpgGrid), once the guide has loaded: without it
   * Down from a programme into such a row dropped the ring onto the channel
   * name, and the next Up jumped from there back out to the programme — not
   * the closest thing on screen (owner, 2026-09-26: "paths going to the
   * closest button"). While the guide loads, rows have no columns at all.
   */
  const countPrograms = useCallback(
    (row: number): number => (programIndex && channels[row] ? Math.max(1, programsIn(row).length) : 0),
    [programsIn, programIndex, channels],
  );

  // The TIME the ring is at, so Up/Down land on what airs then on the next
  // channel. WHY (projector, 2026-09-25): moving by programme NUMBER put Down
  // from a row's 3rd programme on the next row's 3rd — often hours away on
  // screen, "clicks don't go where you expect". Set by Left/Right and by
  // entering a row; kept across Up/Down, as TV guides do.
  const anchorMs = useRef<number | null>(null);
  const anchorOf = useCallback(
    (row: number, col: number): number | null => {
      const program = col >= 0 ? programsIn(row)[col] : undefined;
      if (!program) return null;
      return Math.max(Date.parse(program.start), epgWindowStart(new Date()).getTime());
    },
    [programsIn],
  );
  /** The column in `row` airing at `at`; the nearest one if `at` falls in a gap; the "No information" block (0) if none; -1 before the guide loads. */
  const colAt = useCallback(
    (row: number, at: number): number => {
      const programs = programsIn(row);
      if (programs.length === 0) return countPrograms(row) > 0 ? 0 : -1;
      let best = 0;
      let bestGap = Infinity;
      programs.forEach((program, index) => {
        const start = Date.parse(program.start);
        const end = Date.parse(program.end);
        const gap = at < start ? start - at : at >= end ? at - end + 1 : 0;
        if (gap < bestGap) {
          bestGap = gap;
          best = index;
        }
      });
      return best;
    },
    [programsIn, countPrograms],
  );

  const clampCol = useCallback(
    (row: number, col: number): number => {
      if (col === -1) return -1;
      const count = countPrograms(row);
      if (count === 0) return -1;
      return Math.min(col, count - 1);
    },
    [countPrograms],
  );

  const focusAt = useCallback(
    (row: number, col: number) => {
      if (row < 0 || row >= channels.length) return;
      const nextCol = clampCol(row, col);
      // Idempotent: re-clicking the already-focused target must not re-trigger
      // the scroll/focus pass (which could steal focus from an opened dialog).
      if (row === focus.row && nextCol === focus.col) return;
      movedRef.current = true;
      anchorMs.current = nextCol >= 0 ? anchorOf(row, nextCol) : null;
      setFocus({ row, col: nextCol });
    },
    [channels.length, clampCol, focus, anchorOf],
  );

  /** Returns false when focus could not move (an edge), so the key can pass on. */
  const move = useCallback(
    (dRow: number, dCol: number): boolean => {
      let row = focus.row + dRow;
      if (row < 0) row = 0;
      if (row >= channels.length) row = Math.max(0, channels.length - 1);
      // Left from the channel list is the guide's edge (col -1 clamps to -1).
      if (dCol < 0 && focus.col === -1) return false;
      let col: number;
      // Up/Down inside the grid: the programme airing at the anchored time.
      // Also from the channel list when only an empty row put the ring there
      // (the anchor survives that; Left into the list clears it).
      if (dRow !== 0 && (focus.col >= 0 || anchorMs.current !== null)) {
        if (focus.col >= 0) anchorMs.current ??= anchorOf(focus.row, focus.col);
        col = anchorMs.current === null ? clampCol(row, focus.col) : colAt(row, anchorMs.current);
      } else {
        col = clampCol(row, focus.col + dCol);
      }
      if (row === focus.row && col === focus.col) return false;
      movedRef.current = true;
      lastMoveAt.current = Date.now();
      // Sideways (or into the grid from the channel list) moves the anchor.
      if (dCol !== 0) anchorMs.current = col >= 0 ? anchorOf(row, col) : null;
      setFocus({ row, col });
      // Nothing else: the arrows only move the ring. OK on the focused element
      // is what tunes and raises the detail card.
      return true;
    },
    [focus, clampCol, colAt, anchorOf],
  );

  /**
   * Scroll the guide half a screen through the focused show when it runs past
   * that edge; false when its end (or start) is already on screen, so the
   * arrow moves on to the next show. The time the ring stands for follows the
   * screen, so Up/Down then land on what airs there on the next channel.
   */
  const pageThrough = useCallback(
    (el: HTMLElement, way: 1 | -1): boolean => {
      const scroller = el.closest<HTMLElement>(".epg-grid-rows");
      if (!scroller || focus.col < 0) return false;
      const view = scroller.clientWidth;
      const left = scroller.scrollLeft;
      const past = way > 0 ? el.offsetLeft + el.offsetWidth - (left + view) : left - el.offsetLeft;
      if (past <= 1) return false;
      const next = Math.max(0, left + way * Math.min(past, Math.round(view / 2)));
      scroller.scrollLeft = next;
      const program = programsIn(focus.row)[focus.col];
      const from = anchorOf(focus.row, focus.col);
      if (program && from !== null && el.offsetWidth > 0) {
        const msPerPx = (Date.parse(program.end) - from) / el.offsetWidth;
        anchorMs.current = from + Math.max(0, next - el.offsetLeft) * msPerPx;
      }
      lastMoveAt.current = Date.now();
      return true;
    },
    [focus, programsIn, anchorOf],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "SELECT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }
      // A popup is open, so the guide beneath it is not the viewer's: focus
      // goes back into the popup instead (owner, 2026-09-26: a popup always
      // keeps focus). Focus reached a guide row once when a menu let it slip.
      const popup = document.querySelector<HTMLElement>('[role="dialog"], [role="menu"]');
      if (popup && !popup.contains(target) && event.key.startsWith("Arrow")) {
        event.preventDefault();
        popup.querySelector<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')?.focus();
        return;
      }
      // A show running past the edge of the screen: Left/Right page the guide
      // through it before leaving it, as TiviMate does. Right on a 7-hour show
      // that ran past the 2 hours on screen did nothing at all — no next show
      // was loaded and nothing scrolled (onn box, 2026-10-06).
      if ((event.key === "ArrowRight" || event.key === "ArrowLeft") && pageThrough(target, event.key === "ArrowRight" ? 1 : -1)) {
        event.preventDefault();
        return;
      }
      const moved =
        event.key === "ArrowUp" ? move(-1, 0)
        : event.key === "ArrowDown" ? move(1, 0)
        : event.key === "ArrowLeft" ? move(0, -1)
        : event.key === "ArrowRight" ? move(0, 1)
        : null;
      // Up from the top of the guide: the preview window above it (owner,
      // 2026-10-06), when it is a stop (player/PlayerSurface.tsx).
      if (!moved && event.key === "ArrowUp") {
        const preview = document.querySelector<HTMLElement>("[data-preview-stop]");
        if (preview) {
          event.preventDefault();
          preview.focus();
          return;
        }
      }
      // An arrow that moved nothing passes on to the app-wide D-pad navigation
      // (components/spatialNav.ts) only where the guide has a neighbour: Left
      // from the channel list is the nav rail, Up from the first channel the
      // toolbar. Right at the end of a row and Down at the last channel stay
      // put, as every TV guide does — passed on, they jumped to the nearest
      // control that way, the toolbar's channel-number button at the top right
      // (projector, 2026-09-25: a movie channel lists one film in the 4-hour
      // window, so Right from it left the guide on every press).
      if (moved || event.key === "ArrowRight" || event.key === "ArrowDown") event.preventDefault();
      // Right at the end of what is loaded: load the rest of the guide, so the
      // next Right has somewhere to go.
      if (!moved && event.key === "ArrowRight") requestGuideFar();
    },
    [move, pageThrough],
  );

  // An arrow that finds focus on <body> while the guide is on screen belongs to
  // the guide.
  //
  // WHY (2026-09-25, a viewer holding Down to scroll fast): each step scrolls
  // the virtualized rows and focuses the next element once it is rendered. On
  // the projector a held key outran that — the focused row was recycled before
  // the next one existed, focus fell to <body>, and <body> is where LivePage's
  // quick-zap listens: the next Down tuned the next channel and raised its
  // card. Closing the card handed focus back to <body>, so every later Down
  // zapped again ("once it starts it carries over"), each one a new stream.
  //
  // Capture phase, so it runs before that listener. Not while the player fills
  // the screen (Up/Down zapping there is the feature). With the detail card
  // open the arrow goes back into the card; with any other dialog or menu open
  // it is left alone.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const dRow = event.key === "ArrowUp" ? -1 : event.key === "ArrowDown" ? 1 : 0;
      const dCol = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
      if (dRow === 0 && dCol === 0) return;
      const active = document.activeElement;
      if (active && active !== document.body) return;
      if (document.querySelector(".player-surface--fullscreen")) return;
      // The detail card (MiniEpg) is open: the stray arrow goes back into it.
      // It is redrawn when the channel changes, which is one way focus got lost.
      const card = document.querySelector<HTMLElement>("[data-epg-mini]");
      if (card) {
        event.preventDefault();
        event.stopImmediatePropagation();
        card.querySelector<HTMLElement>("button:not([disabled])")?.focus();
        return;
      }
      if (document.querySelector('[role="dialog"], [role="menu"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      // Nothing focused and no run of presses under way (a fresh screen, or
      // back from full screen): the first press shows where the ring is — the
      // remembered channel — instead of stepping off it before the viewer has
      // seen it. Mid-run (a held key outran the rows, the case above) it moves.
      const ring = document.querySelector<HTMLElement>(targetSelector(focus));
      if (ring && Date.now() - lastMoveAt.current > HELD_RUN_MS) {
        movedRef.current = true;
        ring.focus({ preventScroll: true });
        return;
      }
      // At an edge nothing moves, so the effect below does not run: put focus
      // back on the element the ring is on, or the next arrow lands here again.
      if (!move(dRow, dCol)) document.querySelector<HTMLElement>(targetSelector(focus))?.focus({ preventScroll: true });
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [move, focus]);

  // Wherever focus lands on a guide element — a popup closing hands it back to
  // the row it came from, a click, a restore — that element becomes the guide's
  // position. Out of step, the next arrow moved from where the guide last WAS:
  // after the long-press menu closed on Cartoon Network, Right went to a
  // programme five rows up (projector, 2026-09-26).
  const focusRef = useRef(focus);
  focusRef.current = focus;
  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const el = event.target instanceof HTMLElement ? event.target : null;
      const rowText = el?.dataset.guideRow;
      if (!el || rowText === undefined || !el.closest("[data-guide]")) return;
      const row = Number(rowText);
      const col = el.dataset.guideCol === undefined ? -1 : Number(el.dataset.guideCol);
      if (!Number.isInteger(row) || !Number.isInteger(col)) return;
      const current = focusRef.current;
      if (current.row === row && current.col === col) return;
      anchorMs.current = col >= 0 ? anchorOf(row, col) : null;
      setFocus({ row, col });
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [anchorOf]);

  // Back from full screen: focus goes to the ring (the channel's row), so the
  // viewer sees where they are at once. It was left on <body> (projector,
  // 2026-09-26), with nothing highlighted.
  //
  // When the channel changed in full screen (Up/Down, CH+/CH−, the recent
  // strip), the ring first moves to the channel now playing. It stayed on the
  // one full screen was opened from: zapped FXX → FYI → Gala, Back showed the
  // ring on FXX and Gala marked as playing, two rows apart (projector,
  // 2026-09-29).
  const selectedRef = useRef({ row: initialRow, id: channels[initialRow]?.id ?? null, count: channels.length });
  selectedRef.current = { row: initialRow, id: channels[initialRow]?.id ?? null, count: channels.length };
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    const root = document.documentElement;
    let wasFull = root.classList.contains("player-fullscreen");
    let enteredWith = wasFull ? selectedRef.current.id : null;
    const observer = new MutationObserver(() => {
      const full = root.classList.contains("player-fullscreen");
      if (!wasFull && full) enteredWith = selectedRef.current.id;
      // Opened from the preview window or the toolbar's full-screen button: Back
      // returns there (PlayerSurface), not to a guide row.
      if (wasFull && !full && document.querySelector("[data-fs-opener]")) {
        wasFull = full;
        return;
      }
      if (wasFull && !full) {
        const selected = selectedRef.current;
        if (selected.id !== enteredWith && selected.row >= 0 && selected.row < selected.count && selected.row !== focusRef.current.row) {
          // The focus effect below scrolls the row into view and focuses it.
          movedRef.current = true;
          anchorMs.current = null;
          setFocus({ row: selected.row, col: -1 });
          wasFull = full;
          return;
        }
        requestAnimationFrame(() => {
          const active = document.activeElement;
          if (active && active !== document.body) return;
          if (document.querySelector('[role="dialog"], [role="menu"]')) return;
          document.querySelector<HTMLElement>(targetSelector(focusRef.current))?.focus({ preventScroll: true });
        });
      }
      wasFull = full;
    });
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // Entering the guide from outside lands on the closest guide element that
  // way: Right from the nav rail on the channel BESIDE the rail item, Down
  // from the toolbar on the top row under the control (the category picker's
  // channel, the programme under the channel-number button). Left to the app's
  // navigation, both went to the guide's one focusable element — the
  // remembered row wherever it was, a programme cell far down, or the category
  // picker (projector, 2026-09-26). Left from a row lands on the rail item
  // beside it, so Right comes straight back to that row. Capture phase, ahead
  // of the app's navigation; not with a popup open or the player full screen.
  useEffect(() => {
    const visible = (el: HTMLElement, probeX: number): DOMRect | null => {
      const r = el.getBoundingClientRect();
      if (r.height < 1 || r.width < 1 || r.bottom <= 0 || r.top >= window.innerHeight || r.right <= 0 || r.left >= window.innerWidth) return null;
      // Not one scrolled under the toolbar or the floating rail (Overlay).
      if (typeof document.elementFromPoint === "function") {
        const x = Math.min(Math.max(probeX, r.left + 2), r.right - 2);
        const hit = document.elementFromPoint(x, (r.top + r.bottom) / 2);
        if (!hit || !el.contains(hit)) return null;
      }
      return r;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.key !== "ArrowRight" && event.key !== "ArrowDown") || event.defaultPrevented) return;
      const from = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const fromRail = event.key === "ArrowRight" && !!from?.closest(".navrail");
      // Down from the preview window goes into the guide like Down from the toolbar.
      const fromToolbar = event.key === "ArrowDown" && !!from?.closest(".epg-toolbar, [data-preview-stop]");
      if (!from || (!fromRail && !fromToolbar)) return;
      if (document.querySelector(".player-surface--fullscreen") || document.querySelector('[role="dialog"], [role="menu"]')) return;
      const box = from.getBoundingClientRect();
      // The preview window is a stop between them (owner, 2026-10-06): Right from
      // the top of the rail — the collapse arrow, Home, Live TV, Movies, Series,
      // the owner's list; My List sits level with the window too but goes to the
      // guide — and Down from a toolbar control above it (the categories) land
      // on the window, not the guide.
      const preview = document.querySelector<HTMLElement>("[data-preview-stop]");
      if (preview && !from.closest("[data-preview-stop]")) {
        const p = preview.getBoundingClientRect();
        const besideIt = fromRail && from.matches(PREVIEW_RAIL_ITEMS);
        const aboveIt = !fromRail && box.right > p.left && box.left < p.right;
        if (besideIt || aboveIt) {
          event.preventDefault();
          event.stopImmediatePropagation();
          preview.focus();
          return;
        }
      }
      let best: HTMLElement | null = null;
      let bestScore = Infinity;
      if (fromRail) {
        const middle = (box.top + box.bottom) / 2;
        for (const row of document.querySelectorAll<HTMLElement>("[data-guide] .epg-channel-list [data-guide-row]")) {
          const r = visible(row, box.right + 4);
          if (!r) continue;
          const score = Math.abs((r.top + r.bottom) / 2 - middle);
          if (score < bestScore) {
            bestScore = score;
            best = row;
          }
        }
      } else {
        const middle = (box.left + box.right) / 2;
        for (const el of document.querySelectorAll<HTMLElement>("[data-guide] [data-guide-row]")) {
          const r = visible(el, middle);
          if (!r || r.top < box.bottom - 4) continue;
          // Lined up under the control first, then the nearest down.
          const across = Math.max(0, r.left - box.right, box.left - r.right);
          const score = (across === 0 ? 0 : 100_000 + across) + (r.top - box.bottom);
          if (score < bestScore) {
            bestScore = score;
            best = el;
          }
        }
      }
      const row = best ? Number(best.dataset.guideRow) : NaN;
      const col = best?.dataset.guideCol === undefined ? -1 : Number(best.dataset.guideCol);
      if (!best || !Number.isInteger(row) || !Number.isInteger(col)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      movedRef.current = true;
      lastMoveAt.current = Date.now();
      anchorMs.current = col >= 0 ? anchorOf(row, col) : null;
      if (row === focus.row && col === focus.col) best.focus({ preventScroll: true });
      else setFocus({ row, col });
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [focus, anchorOf]);

  // Opening on a remembered or playing channel (LivePage's save state), or the
  // selection changing before the viewer has touched the guide (a CH+ zap):
  // put the ring and the view on that row. Focus is not taken — the guide
  // never steals it on its own; the first arrow starts from here.
  useEffect(() => {
    if (movedRef.current || initialRow < 0 || initialRow >= channels.length) return;
    setFocus((current) => (current.row === initialRow && current.col === -1 ? current : { row: initialRow, col: -1 }));
    const rail = sharedScrollRef.current;
    if (!rail) return;
    const next = scrollToShow(initialRow, rail.scrollTop, visibleHeight(rail));
    if (next !== null) rail.scrollTop = Math.max(0, Math.min(next, rail.scrollHeight - rail.clientHeight));
  }, [initialRow, channels.length, sharedScrollRef]);

  // Keep the focused row in view (via the shared rail), then focus its element.
  //
  // WHY THIS SCROLLS SO LITTLE (projector, 2026-09-25): every arrow took
  // 0.35-1.2 s to show, and the handler was only 40-110 ms of it — the rest was
  // the frame after. Each press re-centred the guide, so every press scrolled
  // the whole grid: the virtualizer re-rendered its rows, the grid repainted,
  // and the next element was focused on a 50 ms timer with the browser's own
  // scroll-into-view on top. Now a press inside the visible page moves only
  // the ring; the guide scrolls when the ring reaches its edge, one row of the
  // next channels still showing, and only a far jump re-centres.
  useEffect(() => {
    if (!movedRef.current) return;
    const rail = sharedScrollRef.current;
    if (rail) {
      const next = scrollToShow(focus.row, rail.scrollTop, visibleHeight(rail));
      if (next !== null) rail.scrollTop = Math.max(0, Math.min(next, rail.scrollHeight - rail.clientHeight));
    }
    const place = (): boolean => {
      const el = document.querySelector<HTMLElement>(targetSelector(focus));
      if (!el) return false;
      // Never steal focus from an open dialog (program detail, keypad), and
      // don't re-focus an element that already has it. preventScroll: the rail
      // above is the only thing that scrolls the guide.
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.closest('[role="dialog"]')) return true;
      if (el !== active) el.focus({ preventScroll: true });
      revealAcross(el);
      // Checked two frames on, once the virtualizer has redrawn the rows for
      // the new scroll offset — measured sooner, the row is still where it was.
      if (rail) {
        const selector = targetSelector(focus);
        requestAnimationFrame(() => requestAnimationFrame(() => {
          const now = document.querySelector<HTMLElement>(selector);
          if (now && now === document.activeElement) keepOnScreen(now, rail);
        }));
      }
      return true;
    };
    // Usually the element is already drawn (the next row, the next programme).
    if (place()) return;
    let attempts = 0;
    const timer = window.setInterval(() => {
      if (place() || ++attempts > 30) window.clearInterval(timer);
    }, 50);
    return () => window.clearInterval(timer);
  }, [focus, sharedScrollRef]);

  return { focus, focusAt, handleKeyDown };
}

/**
 * How much of the rail is on screen, from its top. The rail can run past the
 * bottom of the screen, and scrolling by its full height let the ring walk off
 * the bottom with nothing moving — "D-pad down doesn't move the screen" in both
 * layouts (owner, 2026-09-26).
 */
function visibleHeight(rail: HTMLElement): number {
  const box = rail.getBoundingClientRect();
  return Math.max(0, Math.min(box.bottom, window.innerHeight) - box.top);
}

/**
 * The safety net under scrollToShow: if the focused row still ends up past the
 * bottom of the screen or under the timeline header, scroll by what is needed
 * — measured on screen, so no layout quirk can leave the ring out of sight.
 */
function keepOnScreen(el: HTMLElement, rail: HTMLElement): void {
  const box = el.getBoundingClientRect();
  const top = rail.getBoundingClientRect().top + EPG_TIMELINE_HEADER_HEIGHT;
  const bottom = Math.min(rail.getBoundingClientRect().bottom, window.innerHeight);
  if (box.bottom > bottom) rail.scrollTop += box.bottom - bottom + CHANNEL_ROW_HEIGHT;
  else if (box.top < top) rail.scrollTop -= top - box.top + CHANNEL_ROW_HEIGHT;
}

/**
 * Scroll the grid sideways so a programme block shows.
 *
 * focus() used to do this, but it also scrolled vertically behind the rail's
 * back, so it runs with preventScroll — and then a programme past the right
 * edge of the 4-hour grid was focused off screen (projector, 2026-09-25). The
 * timeline header follows the grid's own scroll event.
 */
function revealAcross(el: HTMLElement): void {
  const scroller = el.closest<HTMLElement>(".epg-grid-rows");
  if (!scroller) return; // a channel row: nothing to scroll sideways
  const left = el.offsetLeft;
  // A show wider than the screen that is already on it stays where the viewer
  // paged to: jumping back to its start undid every Right through it.
  if (el.offsetWidth > scroller.clientWidth && left < scroller.scrollLeft + scroller.clientWidth && left + el.offsetWidth > scroller.scrollLeft) return;
  const width = Math.min(el.offsetWidth, scroller.clientWidth);
  // Off to the left, it shows its end: Left from the next show onto a long one
  // opened it at its start, hours back (onn box, 2026-10-06). A show narrower
  // than the screen shows whole either way.
  if (left < scroller.scrollLeft) scroller.scrollLeft = Math.max(0, left, left + el.offsetWidth - scroller.clientWidth);
  else if (left + width > scroller.scrollLeft + scroller.clientWidth) scroller.scrollLeft = left + width - scroller.clientWidth;
}

/**
 * Where the rail must scroll so `row` shows, or null when it already does.
 *
 * Rows sit under the timeline header; a row counts as shown with one more row
 * of room past it, so the viewer sees the next channel coming. A row more
 * than a page away (a click, the guide opening on the tuned channel) is
 * centred instead of dragged to an edge.
 */
export function scrollToShow(row: number, scrollTop: number, viewHeight: number): number | null {
  const rowTop = row * CHANNEL_ROW_HEIGHT;
  const rowBottom = rowTop + CHANNEL_ROW_HEIGHT;
  const rowsHeight = viewHeight - EPG_TIMELINE_HEADER_HEIGHT;
  const margin = rowsHeight >= CHANNEL_ROW_HEIGHT * 3 ? CHANNEL_ROW_HEIGHT : 0;
  const far = rowBottom < scrollTop - rowsHeight || rowTop > scrollTop + rowsHeight * 2;
  if (far) return rowTop + EPG_TIMELINE_HEADER_HEIGHT - viewHeight / 2 + CHANNEL_ROW_HEIGHT / 2;
  if (rowTop - margin < scrollTop) return rowTop - margin;
  if (rowBottom + margin > scrollTop + rowsHeight) return rowBottom + margin - rowsHeight;
  return null;
}

/** The element the focus ring is on: a channel row (col -1) or a programme block. */
function targetSelector(focus: { row: number; col: number }): string {
  return focus.col === -1
    ? `.epg-channel-list [data-guide-row="${focus.row}"]`
    : `[data-guide-row="${focus.row}"][data-guide-col="${focus.col}"]`;
}
